import CalendarIcon from "@/assets/svgs/calendar-icon";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { RadioInput } from "@/elements/components/ui/radio-input";
import { Select } from "@/elements/components/ui/select";
import { Textarea } from "@/elements/components/ui/textarea";
import React, { useCallback, useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { useLocalSearchParams, useRouter, type Href } from "expo-router";
import { format } from "date-fns";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { Avatar } from "@/elements/components/ui/avatar";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import goBack from "@/elements/components/utils/go-back";

function LessonDetailsScreen() {
  const {
    childId,
    tutorId,
    timeSlot,
    introCall,
    duration,
    recurring,
    recurringSlots,
  } = useLocalSearchParams<{
    childId: string;
    tutorId: string;
    timeSlot: string;
    introCall?: string;
    duration?: string;
    recurring?: string;
    recurringSlots?: string;
  }>();

  const isRecurring = recurring === "true";
  const parsedRecurringSlots: number[] = useMemo(() => {
    if (!recurringSlots) return [];
    try {
      return JSON.parse(decodeURIComponent(recurringSlots));
    } catch {
      return [];
    }
  }, [recurringSlots]);
  const router = useRouter();
  const [lessonMode, setLessonMode] = useState<"inPerson" | "remote">("remote");
  const [note, setNote] = useState("");
  const [selectedSubject, setSelectedSubject] = useState<Id<"subjects"> | null>(
    null
  );
  const { open } = useSheet(SheetIds.SINGLE_SELECT);
  const { data: child } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getStudent, {
      studentId: childId as Id<"students">,
    }),
    enabled: !!childId,
  });
  const { data: tutor } = useQuery({
    ...convexQuery(api.parent.bookLesson.queries.getTutor, {
      tutorId: tutorId as Id<"tutorProfiles">,
    }),
    enabled: !!tutorId,
  });
  const { data: tutorStudentSubjects = [] } = useQuery(
    convexQuery(
      api.parent.bookLesson.queries.getTutorStudentSubjects,
      tutorId && childId
        ? {
            tutorId: tutorId as Id<"tutorProfiles">,
            studentId: childId as Id<"students">,
          }
        : "skip",
    ),
  );
  const selectedSubjectLabel = useMemo(() => {
    return selectedSubject
      ? tutorStudentSubjects.find((subject) => subject._id === selectedSubject)
          ?.label
      : "Select a subject";
  }, [selectedSubject, tutorStudentSubjects]);

  const handleOpenSubjectSelector = useCallback(async () => {
    const result = await open({
      title: "Select a subject",
      options:
        tutorStudentSubjects.map((subject) => ({
          label: subject.label,
          value: subject._id,
        })) ?? [],
    });

    if (result) {
      setSelectedSubject((result as any).selectedOption as Id<"subjects">);
    }
  }, [open, tutorStudentSubjects]);

  const onConfirm = useCallback(() => {
    if (!selectedSubject || !childId || !tutorId || !timeSlot) {
      return;
    }

    let url = `/booking/lesson-request?childId=${childId}&tutorId=${tutorId}&timeSlot=${timeSlot}&${duration ? `duration=${duration}&` : ""}${introCall ? "introCall=true&" : ""}subjectId=${selectedSubject}&note=${note}&lessonMode=${lessonMode}`;

    if (isRecurring && parsedRecurringSlots.length > 0) {
      url += `&recurring=true&recurringSlots=${encodeURIComponent(JSON.stringify(parsedRecurringSlots))}`;
    }

    router.navigate(url as Href);
  }, [
    selectedSubject,
    childId,
    tutorId,
    timeSlot,
    duration,
    introCall,
    router,
    note,
    lessonMode,
    isRecurring,
    parsedRecurringSlots,
  ]);

  return (
    <View className="flex-1 bg-teachLoop-creamBase gap-6">
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="gap-8">
              <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
                {introCall ? "Book a free intro" : "Book a lesson"}
              </Text>
              <View className="gap-4 border border-gray-200 rounded-lg p-4">
                <View className="flex-row items-start gap-8">
                  <View className="flex-row items-center gap-2">
                    <Avatar size="sm" className="h-8 w-8">
                      <Avatar.Content
                        size="sm"
                        labelSize="sm"
                        label={tutor?.name ?? ""}
                        source={
                          tutor?.imageUrl ? { uri: tutor.imageUrl } : undefined
                        }
                      />
                    </Avatar>
                    <View>
                      <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                        Tutor
                      </Text>
                      <Text className="font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]">
                        {tutor?.name}
                      </Text>
                    </View>
                  </View>

                  <View className="flex-row items-center gap-2">
                    <Avatar size="sm" className="h-8 w-8">
                      <Avatar.Content
                        size="sm"
                        labelSize="sm"
                        label={child?.name ?? ""}
                        source={
                          child?.imageUrl ? { uri: child.imageUrl } : undefined
                        }
                      />
                    </Avatar>
                    <View>
                      <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                        Student
                      </Text>
                      <Text className="font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]">
                        {child?.name}
                      </Text>
                    </View>
                  </View>
                </View>

                <View className="flex-row items-start gap-4">
                  <CalendarIcon />
                  <View className="flex-1">
                    {isRecurring && parsedRecurringSlots.length > 0 ? (
                      <View className="gap-1">
                        <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-black">
                          Recurring lessons ({parsedRecurringSlots.length})
                        </Text>
                        {parsedRecurringSlots
                          .sort((a, b) => a - b)
                          .map((slot) => (
                            <Text
                              key={slot}
                              className="text-sm leading-[1.5] font-geologica-regular text-black"
                            >
                              {format(new Date(slot), "EEE, dd MMM yyyy")} at{" "}
                              {format(new Date(slot), "H:mm")}
                            </Text>
                          ))}
                      </View>
                    ) : (
                      <View>
                        <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-black">
                          {format(
                            new Date(Number(timeSlot)),
                            "EEEE, MMMM d, yyyy"
                          )}
                        </Text>
                        <Text className="text-sm leading-[1.5] font-geologica-regular text-black">
                          {format(new Date(Number(timeSlot)), "H:mm")}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            </View>
            <View className="h-6" />
            <View className="gap-6">
              <View className="gap-4">
                <Text className="text-[16px] leading-[1.5] font-geologica-medium font-medium text-black">
                  Lesson mode
                </Text>
                <View className="gap-2">
                  <View className="flex-row gap-2">
                    <RadioInput
                      disabled={!!introCall}
                      onPress={() => setLessonMode("inPerson")}
                    >
                      <RadioInput.Label>In Person</RadioInput.Label>
                      <RadioInput.Icon checked={lessonMode === "inPerson"} />
                    </RadioInput>
                    <RadioInput onPress={() => setLessonMode("remote")}>
                      <RadioInput.Label>Remote</RadioInput.Label>
                      <RadioInput.Icon checked={lessonMode === "remote"} />
                    </RadioInput>
                  </View>
                  {introCall && (
                    <Text className="text-sm leading-[1.5] font-geologica-regular text-black">
                      Intro session is only over a zoom call{" "}
                    </Text>
                  )}
                </View>
              </View>
              <View className="gap-4">
                <Text className="text-base leading-[1.5] font-geologica-medium font-medium text-black">
                  Choose a subject
                </Text>
                <View className="flex-row gap-2">
                  <Select
                    disabled={tutorStudentSubjects.length === 0}
                    size="md"
                    onPress={handleOpenSubjectSelector}
                  >
                    <Select.Label size="md">
                      {selectedSubject
                        ? selectedSubjectLabel
                        : "Select a subject"}
                    </Select.Label>
                    <Select.DownArrow />
                  </Select>
                </View>
              </View>
              <View className="gap-4">
                <Text className="text-[16px] leading-[1.5] font-geologica-medium font-medium text-black">
                  Add a note
                </Text>
                <View className="flex-row gap-2">
                  <Textarea
                    className="font-geologica-regular text-sm text-black font-normal py-2 px-3"
                    placeholder="Please share any additional information that you would like the tutor to know."
                    size="md"
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                    value={note}
                    onChangeText={setNote}
                  />
                </View>
              </View>
            </View>
            <View className="h-8" />
            <Button
              disabled={!selectedSubject || !childId || !tutorId || !timeSlot}
              className="w-full"
              size="md"
              intent="primary"
              onPress={onConfirm}
            >
              <Button.Text size="md">Confirm</Button.Text>
            </Button>
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default LessonDetailsScreen;
